import type { AssignableRole, GroupSummary, PersonDetail, Role } from '@ralph/contracts'
import clsx from 'clsx'
import { KeyRound, Search, TriangleAlert, UsersRound } from 'lucide-react'
import { useId, useState } from 'react'
import { ApiRequestError } from '@/api/client.ts'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { rankDisclosure } from '@/components/people/people-plane.ts'
import { Notice } from '@/components/ui/admin-chrome.tsx'
import { Dialog, DialogFooter } from '@/components/ui/Dialog.tsx'
import { Button, Input, Select, Spinner } from '@/components/ui/primitives.tsx'
import { useAddToGroup, useGroups } from '@/hooks/use-groups.ts'
import {
  useDeactivatePerson,
  useDeactivationPreview,
  useGrantRole,
  useReactivatePerson,
  useRevokeRole
} from '@/hooks/use-people.ts'
import { describeApiError, describeIssue, useI18n } from '@/i18n/index.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * The dialogs of the people plane, and the one thing they have in common.
 *
 * **Every refusal here is the server's.** The last-administrator invariant, self-demotion, the
 * typed-name gate and four-eyes are all decided in one place and arrive as codes; the dialogs
 * render what came back rather than predicting it. A disabled button may agree with the server,
 * and never substitutes for it — which is what keeps the surface honest when a rule changes
 * underneath it, and is why the typed-name field appears in response to
 * `confirm_name_required` as well as ahead of it.
 */

const ASSIGNABLE: AssignableRole[] = ['auditor', 'operator', 'admin']

/** Roles worth a sentence of justification: the ones somebody will ask about later. */
const REASON_REQUIRED: Role[] = ['admin', 'operator']

/**
 * The refusal, in the reader's language.
 *
 * The order is the point. A `messageKey` wins outright — the server named WHICH refusal this
 * is and the catalogue has that sentence with its specifics intact (PRD-27 D-4). Only when
 * there is no key do the two coarse codes get a sentence of this file's own, and they are
 * worth one because `conflict` and `not_found` mean exactly one thing on these five dialogs.
 *
 * `precondition_failed` deliberately gets no such branch: the last-administrator invariant,
 * self-demotion and four-eyes all share it, and until each is keyed the server's own sentence
 * is the only thing that tells them apart. That is what `describeApiError` falls back to, and
 * why the English `message` is never removed from the wire.
 */
const messageFor = (t: Messages, failure: unknown): string => {
  // Nothing refused anything: a dropped connection, an aborted fetch. The browser's own words
  // are about sockets, and what this dialog is about is a change that was not saved.
  if (!(failure instanceof ApiRequestError)) return t.people.errors.generic

  const issue = failure.issues[0]
  const named = issue ? describeIssue(t, issue) : null
  if (named !== null) return named
  if (failure.code === 'conflict') return t.people.errors.conflict
  if (failure.code === 'not_found') return t.people.errors.notFound
  return describeApiError(t, failure)
}

// ---------------------------------------------------------------------------

/**
 * Giving a role, or overriding one the directory sends.
 *
 * The rank disclosure is generated from the ladder rather than written per role, so the sentence
 * cannot drift from `ROLE_RANK` — which is the rule `hasAtLeast` actually applies, and which
 * nothing in the product stated before this screen existed.
 */
export const GrantDialog = ({
  person,
  effect,
  presetRole,
  onClose
}: {
  person: PersonDetail
  effect: 'allow' | 'deny'
  presetRole?: Role
  onClose: () => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.people
  const titleId = useId()
  const roleId = useId()
  const reasonId = useId()
  const dateId = useId()

  const [role, setRole] = useState<AssignableRole>(
    (presetRole && presetRole !== 'user' ? presetRole : 'auditor') as AssignableRole
  )
  const [reason, setReason] = useState('')
  const [expires, setExpires] = useState<string>('')
  const [bounded, setBounded] = useState(false)

  const grant = useGrantRole()
  const disclosure = rankDisclosure(fmt, role)
  const reasonMissing = effect === 'allow' && REASON_REQUIRED.includes(role) && reason.trim() === ''

  const submit = () => {
    grant.mutate(
      {
        userId: person.id,
        role,
        effect,
        reason: reason.trim(),
        expiresAt: bounded && expires !== '' ? new Date(`${expires}T23:59:59`).toISOString() : null
      },
      { onSuccess: (result) => (result.pendingApproval ? undefined : onClose()) }
    )
  }

  const pending = grant.data?.pendingApproval === true

  return (
    <Dialog
      titleId={titleId}
      icon={<KeyRound size={14} strokeWidth={2} />}
      title={
        effect === 'deny'
          ? t.grantDialog.denyTitle(person.displayName, role)
          : t.grantDialog.title(person.displayName, role)
      }
      lead={effect === 'deny' ? t.grantDialog.denyLead : undefined}
      onClose={onClose}
    >
      <div className="space-y-3.5 px-5 pt-4">
        {pending ? (
          <Notice title={t.grantDialog.pendingApproval} icon={<TriangleAlert size={12} aria-hidden />} />
        ) : (
          <>
            <div>
              <label htmlFor={roleId} className="eyebrow">
                {t.grantDialog.roleLabel}
              </label>
              <div className="mt-1.5">
                <Select
                  id={roleId}
                  value={role}
                  onChange={(event) => setRole(event.target.value as AssignableRole)}
                  disabled={presetRole !== undefined}
                >
                  {ASSIGNABLE.map((value) => (
                    <option key={value} value={value}>
                      {t.roleLabel[value]}
                    </option>
                  ))}
                </Select>
              </div>
              <p className="mt-1.5 text-[12.5px] leading-[1.5] text-fg-muted">{t.grantDialog.meaning(role)}</p>
              {disclosure && effect === 'allow' && (
                <p className="mt-1 text-[12.5px] leading-[1.5] text-fg-subtle">{disclosure}</p>
              )}
            </div>

            {effect === 'allow' && (
              <fieldset>
                <legend className="eyebrow">{t.grantDialog.until}</legend>
                <div className="mt-1.5 flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-[13px]">
                    <input type="radio" name="bounded" checked={!bounded} onChange={() => setBounded(false)} />
                    {t.grantDialog.untilForever}
                  </label>
                  <label className="inline-flex items-center gap-2 text-[13px]">
                    <input type="radio" name="bounded" checked={bounded} onChange={() => setBounded(true)} />
                    {t.grantDialog.untilDate}
                  </label>
                </div>
                {bounded && (
                  <div className="mt-2 max-w-[12rem]">
                    <label htmlFor={dateId} className="sr-only">
                      {t.grantDialog.dateLabel}
                    </label>
                    <Input
                      id={dateId}
                      type="date"
                      value={expires}
                      onChange={(event) => setExpires(event.target.value)}
                    />
                  </div>
                )}
              </fieldset>
            )}

            <div>
              <label htmlFor={reasonId} className="eyebrow">
                {t.grantDialog.reasonLabel}
              </label>
              <div className="mt-1.5">
                <Input
                  id={reasonId}
                  value={reason}
                  placeholder={t.grantDialog.reasonPlaceholder}
                  onChange={(event) => setReason(event.target.value)}
                />
              </div>
              <p className="mt-1 text-[12px] text-fg-subtle">
                {reasonMissing ? t.grantDialog.reasonRequired : t.grantDialog.reasonHint}
              </p>
            </div>
          </>
        )}

        {grant.isError && (
          <div role="alert">
            <Notice title={messageFor(fmt.t, grant.error)} icon={<TriangleAlert size={12} aria-hidden />} />
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {pending ? t.grantDialog.close : t.grantDialog.cancel}
        </Button>
        {!pending && (
          <Button
            variant={effect === 'deny' ? 'danger' : 'primary'}
            disabled={grant.isPending || reasonMissing || (bounded && expires === '')}
            onClick={submit}
          >
            {grant.isPending
              ? t.grantDialog.working
              : effect === 'deny'
                ? t.grantDialog.confirmDeny
                : t.grantDialog.confirm(role)}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------

/** Taking a grant back, or lifting an override. A confirmation with a reason, not a dialog of state. */
export const RemoveRoleDialog = ({
  person,
  role,
  effect,
  onClose
}: {
  person: PersonDetail
  role: Role
  effect: 'allow' | 'deny'
  onClose: () => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.people
  const titleId = useId()
  const reasonId = useId()
  const [reason, setReason] = useState('')
  const revoke = useRevokeRole()

  return (
    <Dialog
      titleId={titleId}
      icon={<KeyRound size={14} strokeWidth={2} />}
      title={t.removeDialog.title(person.displayName, role)}
      lead={t.removeDialog.lead}
      onClose={onClose}
    >
      <div className="space-y-3.5 px-5 pt-4">
        <div>
          <label htmlFor={reasonId} className="eyebrow">
            {t.removeDialog.reasonLabel}
          </label>
          <div className="mt-1.5">
            <Input id={reasonId} value={reason} onChange={(event) => setReason(event.target.value)} />
          </div>
        </div>

        {revoke.isError && (
          <div role="alert">
            <Notice title={messageFor(fmt.t, revoke.error)} icon={<TriangleAlert size={12} aria-hidden />} />
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.grantDialog.cancel}
        </Button>
        <Button
          variant="danger"
          disabled={revoke.isPending}
          onClick={() =>
            revoke.mutate({ userId: person.id, role, effect, reason: reason.trim() }, { onSuccess: onClose })
          }
        >
          {revoke.isPending ? t.removeDialog.working : t.removeDialog.confirm}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------

/**
 * Switching an account off.
 *
 * Every number in the consequence list is a query the server ran against the same predicates the
 * write will use — a consequence written in prose is a consequence that goes stale. The typed
 * name is the existing `confirm_name_required` refusal: the field is shown up front because the
 * rule is known, and it is shown again if the server raises it, so the two can never disagree.
 */
export const DeactivateDialog = ({ person, onClose }: { person: PersonDetail; onClose: () => void }) => {
  const fmt = useI18n()
  const t = fmt.t.people
  const titleId = useId()
  const reasonId = useId()
  const confirmId = useId()
  const [reason, setReason] = useState('')
  const [typed, setTyped] = useState('')

  const preview = useDeactivationPreview(person.id)
  const deactivate = useDeactivatePerson()
  const data = preview.data

  const nameMatches = typed.trim().toLowerCase() === person.displayName.trim().toLowerCase()

  return (
    <Dialog
      titleId={titleId}
      icon={<TriangleAlert size={14} strokeWidth={2} />}
      title={t.deactivateDialog.title(person.displayName)}
      onClose={onClose}
    >
      <div className="space-y-3.5 px-5 pt-4">
        {preview.isLoading && (
          <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
            <Spinner /> {t.deactivateDialog.loading}
          </p>
        )}

        {data?.isLastAdmin && (
          <Notice title={t.deactivateDialog.lastAdmin} icon={<TriangleAlert size={12} aria-hidden />} />
        )}

        {data && (
          <ul className="space-y-1.5 rounded-tile bg-fg/[0.02] px-3 py-2.5 text-[13px] leading-[1.5]">
            <li>{t.deactivateDialog.sessions(fmt, data.sessions)}</li>
            {data.owns.agents.length > 0 && <li>{t.deactivateDialog.agents(fmt, data.owns.agents.length)}</li>}
            {data.owns.knowledgeBases.length > 0 && (
              <li>{t.deactivateDialog.bases(fmt, data.owns.knowledgeBases.length)}</li>
            )}
            {data.groupsLeftWithoutManagers.length > 0 && (
              <li>
                {t.deactivateDialog.unmanagedGroups(
                  fmt,
                  data.groupsLeftWithoutManagers.map((group) => group.name)
                )}
              </li>
            )}
            {data.owns.conversationCount > 0 && (
              <li>{t.deactivateDialog.conversations(fmt, data.owns.conversationCount)}</li>
            )}
            {data.liveGrants > 0 && <li>{t.deactivateDialog.grants(fmt, data.liveGrants)}</li>}
            <li className="text-fg-muted">{t.deactivateDialog.nothingDeleted}</li>
          </ul>
        )}

        <div>
          <label htmlFor={reasonId} className="eyebrow">
            {t.deactivateDialog.reasonLabel}
          </label>
          <div className="mt-1.5">
            <Input
              id={reasonId}
              value={reason}
              placeholder={t.deactivateDialog.reasonPlaceholder}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>

        <div>
          <label htmlFor={confirmId} className="eyebrow">
            {t.deactivateDialog.confirmNameLabel(person.displayName)}
          </label>
          <div className="mt-1.5">
            <Input
              id={confirmId}
              value={typed}
              placeholder={t.deactivateDialog.confirmNamePlaceholder}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
        </div>

        {deactivate.isError && (
          <div role="alert">
            <Notice title={messageFor(fmt.t, deactivate.error)} icon={<TriangleAlert size={12} aria-hidden />} />
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.deactivateDialog.cancel}
        </Button>
        <Button
          variant="danger"
          disabled={deactivate.isPending || !nameMatches || data?.isLastAdmin === true}
          onClick={() =>
            deactivate.mutate(
              { userId: person.id, reason: reason.trim(), confirmName: typed.trim() },
              { onSuccess: onClose }
            )
          }
        >
          {deactivate.isPending ? t.deactivateDialog.working : t.deactivateDialog.confirm}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------

export const ReactivateDialog = ({ person, onClose }: { person: PersonDetail; onClose: () => void }) => {
  const fmt = useI18n()
  const t = fmt.t.people
  const titleId = useId()
  const reasonId = useId()
  const [reason, setReason] = useState('')
  const reactivate = useReactivatePerson()

  return (
    <Dialog
      titleId={titleId}
      icon={<KeyRound size={14} strokeWidth={2} />}
      title={t.reactivateDialog.title(person.displayName)}
      lead={t.reactivateDialog.lead}
      onClose={onClose}
    >
      <div className="space-y-3.5 px-5 pt-4">
        <div>
          <label htmlFor={reasonId} className="eyebrow">
            {t.reactivateDialog.reasonLabel}
          </label>
          <div className="mt-1.5">
            <Input id={reasonId} value={reason} onChange={(event) => setReason(event.target.value)} />
          </div>
        </div>

        {reactivate.isError && (
          <div role="alert">
            <Notice title={messageFor(fmt.t, reactivate.error)} icon={<TriangleAlert size={12} aria-hidden />} />
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.grantDialog.cancel}
        </Button>
        <Button
          variant="primary"
          disabled={reactivate.isPending}
          onClick={() => reactivate.mutate({ userId: person.id, reason: reason.trim() }, { onSuccess: onClose })}
        >
          {reactivate.isPending ? t.reactivateDialog.working : t.reactivateDialog.confirm}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------

/**
 * Adding one person to a group, from their profile (PRD-26 D-4).
 *
 * The inverse of the group page's *Add people*: there the group is fixed and people are chosen;
 * here the person is fixed and the group is what gets picked. Groups they are already in stay in
 * the list, marked and not selectable, rather than vanishing — an administrator scanning for
 * "Finance" who does not find it would otherwise wonder whether it exists.
 *
 * A refusal is the server's (a deactivated account, the service principal) and is said in the
 * groups plane's words, so the two surfaces explain the same refusal the same way.
 */
export const AddToGroupDialog = ({ person, onClose }: { person: PersonDetail; onClose: () => void }) => {
  const fmt = useI18n()
  const t = fmt.t.people
  const groupCopy = fmt.t.groups
  const titleId = useId()
  const searchId = useId()
  const radioName = useId()
  const [q, setQ] = useState('')
  const [chosen, setChosen] = useState<GroupSummary | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)

  const query = q.trim()
  const groups = useGroups({ ...(query === '' ? {} : { q: query }), scope: 'all', limit: 50 })
  const add = useAddToGroup()
  const memberOf = new Set(person.groups.map((group) => group.id))
  const items = groups.data?.items ?? []
  const total = groups.data?.total ?? 0

  const submit = () => {
    if (!chosen) return
    setRefusal(null)
    add.mutate(
      { groupId: chosen.id, userIds: [person.id] },
      {
        onSuccess: (result) => {
          const refused = result.refused.find((entry) => entry.id === person.id)
          if (refused) setRefusal(t.person.addToGroupRefused(person.displayName, groupCopy.add.refused[refused.reason]))
          else onClose()
        }
      }
    )
  }

  return (
    <Dialog
      titleId={titleId}
      icon={<UsersRound size={14} strokeWidth={2} />}
      title={t.person.addToGroupTitle(person.displayName)}
      lead={t.person.addToGroupLead}
      onClose={onClose}
    >
      <div className="space-y-3.5 px-5 pt-4">
        <div>
          <label htmlFor={searchId} className="eyebrow">
            {t.person.addToGroupSearch}
          </label>
          <div className="relative mt-1.5">
            <Search
              size={14}
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
            />
            <Input
              id={searchId}
              value={q}
              autoFocus
              placeholder={t.person.addToGroupPlaceholder}
              className="pl-8"
              onChange={(event) => setQ(event.target.value)}
            />
          </div>
        </div>

        {groups.isLoading ? (
          <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
            <Spinner /> {groupCopy.page.loading}
          </p>
        ) : groups.isError ? (
          // Not "There are no groups yet": a failed read is not an empty list.
          <p role="alert" className="text-[13px] text-alert">
            {t.person.addToGroupFailed}
          </p>
        ) : items.length === 0 ? (
          <p className="text-[13px] text-fg-subtle">
            {query === '' ? t.person.addToGroupNone : t.person.addToGroupNoMatch(query)}
          </p>
        ) : (
          <fieldset>
            <legend className="sr-only">{t.person.addToGroupSearch}</legend>
            <ul className="max-h-[18rem] space-y-1.5 overflow-y-auto pr-0.5">
              {items.map((group) => {
                const already = memberOf.has(group.id)
                return (
                  <li key={group.id}>
                    <label
                      className={clsx(
                        'flex items-center gap-3 rounded-tile border px-3 py-2 text-[13px]',
                        'transition-[border-color,background-color] duration-150',
                        already
                          ? 'cursor-not-allowed border-rule text-fg-subtle'
                          : chosen?.id === group.id
                            ? 'cursor-pointer border-rule-strong bg-card-solid'
                            : 'cursor-pointer border-rule hover:border-rule-strong'
                      )}
                    >
                      <input
                        type="radio"
                        name={radioName}
                        value={group.id}
                        checked={chosen?.id === group.id}
                        disabled={already}
                        onChange={() => setChosen(group)}
                      />
                      <GroupAvatar group={group} size={20} />
                      <span className="min-w-0 flex-1">
                        <span className={clsx('block truncate', !already && 'font-semibold text-fg')}>
                          {group.name}
                        </span>
                        <span className="readout block text-fg-subtle">
                          {t.person.groupMembers(fmt, group.memberCount)}
                        </span>
                      </span>
                      {already && <span className="readout shrink-0 text-fg-subtle">{t.person.alreadyIn}</span>}
                    </label>
                  </li>
                )
              })}
            </ul>
            {total > items.length && (
              <p className="mt-2 text-[12px] text-fg-subtle">{t.person.addToGroupMore(fmt, items.length, total)}</p>
            )}
          </fieldset>
        )}

        {refusal && (
          <div role="alert">
            <Notice title={refusal} icon={<TriangleAlert size={12} aria-hidden />} />
          </div>
        )}
        {add.isError && (
          <div role="alert">
            <Notice title={messageFor(fmt.t, add.error)} icon={<TriangleAlert size={12} aria-hidden />} />
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.grantDialog.cancel}
        </Button>
        <Button variant="primary" disabled={chosen === null || add.isPending} onClick={submit}>
          {add.isPending ? t.person.addToGroupWorking : t.person.addToGroupSubmit}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
